'use client';

import { c as _c } from "react/compiler-runtime";
import ShimmerLogo from '@/app/[locale]/(worlds)/[roomId]/components/ShimmerLogo';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';

// Generate random star positions using fixed large range like original SCSS
const generateShadows = (count: number, radius: number, blur: boolean = true) => {
  let shadow = '';
  for (let i = 0; i < count; i++) {
    const x = Math.random() * 2000;
    const y = Math.random() * 2000;
    if (blur) {
      const blurRadius = radius * 0.1 + Math.random() * radius;
      shadow += `${x}px ${y}px ${blurRadius}px ${radius * 0.1}px #C5C5C5`;
    } else {
      shadow += `${x}px ${y}px #C5C5C5`;
    }
    if (i < count - 1) shadow += ', ';
  }
  return shadow;
};
type WorldSplashProps = {
  isVisible: boolean;
  className?: string;
};
const WorldSplash = t0 => {
  const $ = _c(49);
  const {
    isVisible,
    className
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      small: "",
      medium: "",
      large: "",
      largeClear: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [shadows, setShadows] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setShadows({
        small: generateShadows(700, 1, true),
        medium: generateShadows(300, 2, true),
        large: generateShadows(20, 3, true),
        largeClear: generateShadows(10, 3, false)
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const updateShadows = t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      updateShadows();
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  useEventListener("resize", updateShadows);
  let t5;
  if ($[4] !== className) {
    t5 = cn("h-full w-full overflow-hidden", className);
    $[4] = className;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      background: "radial-gradient(ellipse at bottom, #1b2735 0%, #090a0f 100%)"
    };
    t7 = {
      opacity: 0,
      display: "block"
    };
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  const t8 = isVisible ? 1 : 0;
  const t9 = isVisible ? "block" : "none";
  let t10;
  if ($[8] !== t8 || $[9] !== t9) {
    t10 = {
      opacity: t8,
      display: t9
    };
    $[8] = t8;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1]
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== shadows.small) {
    t12 = {
      left: "500px",
      top: "500px",
      boxShadow: shadows.small
    };
    $[12] = shadows.small;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== shadows.small) {
    t13 = <div className="absolute h-px w-px bg-transparent" style={{
      top: "2000px",
      boxShadow: shadows.small
    }} />;
    $[14] = shadows.small;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t12 || $[17] !== t13) {
    t14 = <div className="absolute h-px w-px animate-[stars-float-up_300s_linear_infinite] rounded-full bg-transparent blur-[0.5px]" style={t12}>{t13}</div>;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== shadows.medium) {
    t15 = {
      boxShadow: shadows.medium
    };
    $[19] = shadows.medium;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== shadows.medium) {
    t16 = <div className="absolute h-0.5 w-0.5 bg-transparent" style={{
      top: "2000px",
      boxShadow: shadows.medium
    }} />;
    $[21] = shadows.medium;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t15 || $[24] !== t16) {
    t17 = <div className="absolute h-0.5 w-0.5 animate-[stars-float-up_200s_linear_infinite] rounded-full bg-transparent blur-[0.5px]" style={t15}>{t16}</div>;
    $[23] = t15;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== shadows.large) {
    t18 = {
      boxShadow: shadows.large
    };
    $[26] = shadows.large;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== shadows.large) {
    t19 = <div className="absolute h-[3px] w-[3px] bg-transparent" style={{
      top: "2000px",
      boxShadow: shadows.large
    }} />;
    $[28] = shadows.large;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t18 || $[31] !== t19) {
    t20 = <div className="relative h-[3px] w-[3px] animate-[stars-float-up_100s_linear_infinite] rounded-full bg-transparent" style={t18}>{t19}</div>;
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== shadows.largeClear) {
    t21 = {
      boxShadow: shadows.largeClear
    };
    $[33] = shadows.largeClear;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== shadows.largeClear) {
    t22 = <div className="absolute h-[3px] w-[3px] bg-transparent" style={{
      top: "2000px",
      boxShadow: shadows.largeClear
    }} />;
    $[35] = shadows.largeClear;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t21 || $[38] !== t22) {
    t23 = <div className="relative h-[3px] w-[3px] animate-[stars-float-up_100s_linear_infinite] rounded-full bg-transparent" style={t21}>{t22}</div>;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  let t25;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="absolute inset-0 z-interactive flex items-center justify-center"><div className="relative"><ShimmerLogo /></div></div>;
    t25 = <style jsx={true} scoped={true}>{"\n        @keyframes stars-float-up {\n          from {\n            transform: translateY(0px);\n          }\n          to {\n            transform: translateY(-6000px);\n          }\n        }\n      "}</style>;
    $[40] = t24;
    $[41] = t25;
  } else {
    t24 = $[40];
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t10 || $[43] !== t14 || $[44] !== t17 || $[45] !== t20 || $[46] !== t23 || $[47] !== t5) {
    t26 = <motion.div ref={containerRef} className={t5} style={t6} initial={t7} animate={t10} transition={t11}>{t14}{t17}{t20}{t23}{t24}{t25}</motion.div>;
    $[42] = t10;
    $[43] = t14;
    $[44] = t17;
    $[45] = t20;
    $[46] = t23;
    $[47] = t5;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  return t26;
};
export default WorldSplash;